import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track, getUTMParams } from "@/lib/analytics";
import TornDivider from "./TornDivider";
import HandArrow from "./HandArrow";

export const CLUB_MEMBER_KEY = "ungrip_club_member";
const CLOSED_KEY = "ungrip_popup_closed_at";
const SEEN_KEY = "ungrip_popup_seen";

function shouldShow(cfg) {
  if (!cfg?.enabled) return false;
  try {
    if (localStorage.getItem(CLUB_MEMBER_KEY)) return false;
    if (sessionStorage.getItem(SEEN_KEY)) return false;
    const closedAt = Number(localStorage.getItem(CLOSED_KEY) || 0);
    const days = Number(cfg.suppress_days ?? 7);
    if (closedAt && Date.now() - closedAt < days * 86400000) return false;
  } catch (e) {
    return false;
  }
  return true;
}

export default function ClubPopup({ settings }) {
  const cfg = settings?.club_popup;
  const t = settings?.club || {};
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);
  const dialogRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!shouldShow(cfg)) return undefined;
    const id = setTimeout(() => {
      if (!shouldShow(cfg)) return;
      sessionStorage.setItem(SEEN_KEY, "1");
      setOpen(true);
      track("club_popup_view", {});
    }, Number(cfg.delay_seconds ?? 3) * 1000);
    return () => clearTimeout(id);
  }, [cfg]);

  const close = useCallback((reason = "close") => {
    setOpen(false);
    if (status !== "success") {
      localStorage.setItem(CLOSED_KEY, String(Date.now()));
      track("club_popup_close", { reason });
    }
  }, [status]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape") close("escape");
      if (e.key === "Tab" && dialogRef.current) {
        const els = dialogRef.current.querySelectorAll("button, a, input");
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.(); };
  }, [open, close]);

  const onType = (v) => {
    setEmail(v);
    if (!started) { setStarted(true); track("club_popup_signup_start", {}); }
  };

  const submit = async (e) => {
    e.preventDefault();
    track("club_popup_cta_click", {});
    if (!consent) { setError("Per entrare serve il tuo consenso marketing."); return; }
    setStatus("loading");
    setError("");
    try {
      await api.post("/club/subscribe", {
        email, marketing_consent: true,
        consent_version: settings?.gdpr?.consent_version || "1.0",
        source: "homepage_popup", ...getUTMParams(),
      });
      localStorage.setItem(CLUB_MEMBER_KEY, "1");
      setStatus("success");
      track("club_popup_signup_success", {});
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] bg-black/75 flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) close("overlay"); }}
          data-testid="club-popup-overlay"
        >
          <motion.div
            ref={dialogRef}
            role="dialog" aria-modal="true" aria-labelledby="club-popup-title"
            initial={{ y: 30, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-[#0C0C0C] border-2 border-[#FFC20E] grain"
            data-testid="club-popup"
          >
            <TornDivider topColor="#FFC20E" bottomColor="#0C0C0C" />
            <button
              onClick={() => close("x")}
              aria-label="Chiudi"
              className="absolute top-2 right-2 z-10 w-10 h-10 rounded-full bg-[#0C0C0C] text-[#FFC20E] flex items-center justify-center hover:bg-[#FFC20E] hover:text-[#0C0C0C] transition-colors duration-200"
              data-testid="club-popup-close"
            >
              <X size={20} />
            </button>
            <div className="relative z-10 px-6 sm:px-10 pb-8 pt-4">
              {status !== "success" ? (
                <PopupForm t={t} email={email} onType={onType} consent={consent} setConsent={setConsent}
                  submit={submit} status={status} error={error} inputRef={inputRef} />
              ) : (
                <div data-testid="club-popup-success">
                  <h2 id="club-popup-title" className="font-display font-black uppercase display-tight text-6xl sm:text-7xl text-[#FFC20E]">
                    {t.success_headline || "SEI DENTRO."}
                  </h2>
                  <p className="mt-4 text-white/80 text-lg">{t.success_copy}</p>
                  <button
                    onClick={() => close("success")}
                    className="mt-6 rounded-full bg-[#FFC20E] text-[#0C0C0C] font-display font-extrabold uppercase tracking-wide px-7 py-3.5 hover:scale-[1.03] transition-transform duration-200"
                    data-testid="club-popup-back"
                  >
                    {t.success_cta || "TORNA A MORDERE"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PopupForm({ t, email, onType, consent, setConsent, submit, status, error, inputRef }) {
  return (
    <div>
      <span className="font-display font-bold uppercase tracking-[0.4em] text-sm text-[#FFC20E]">{t.label || "UNGRIP CLUB"}</span>
      <h2 id="club-popup-title" className="font-display font-black uppercase display-tight text-5xl sm:text-6xl text-white mt-2" data-testid="club-popup-headline">
        {t.headline}
      </h2>
      <p className="mt-4 text-white/70">{t.body}</p>
      <div className="mt-3 flex items-center gap-2">
        <span className="font-hand text-2xl font-bold text-[#FFC20E] -rotate-2">{t.microcopy}</span>
        <HandArrow className="w-10 h-7 rotate-[100deg]" color="#FFC20E" />
      </div>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
        <input
          ref={inputRef} type="email" required value={email}
          onChange={(e) => onType(e.target.value)}
          placeholder={t.input_placeholder || "La tua email"}
          aria-label="Email"
          className="rounded-full bg-[#141414] border border-[#333] px-5 py-3.5 text-white placeholder:text-white/40 font-display font-bold uppercase tracking-wide outline-none focus:border-[#FFC20E] transition-colors"
          data-testid="club-popup-email"
        />
        <label className="flex items-start gap-2 text-white/60 text-xs cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#FFC20E]" data-testid="club-popup-consent" />
          <span>
            Acconsento a ricevere comunicazioni marketing da UNGRIP. Puoi disiscriverti in qualsiasi momento.{" "}
            <Link to="/privacy" className="underline hover:text-[#FFC20E]" data-testid="club-popup-privacy-link">Privacy Policy</Link>
          </span>
        </label>
        <button
          type="submit" disabled={status === "loading"}
          className="rounded-full bg-[#FFC20E] text-[#0C0C0C] font-display font-extrabold uppercase tracking-wide px-7 py-3.5 hover:scale-[1.02] transition-transform duration-200 disabled:opacity-60"
          data-testid="club-popup-submit"
        >
          {status === "loading" ? "…" : t.cta || "FAMMI ENTRARE"}
        </button>
      </form>
      {error && <p className="mt-3 text-[#E63946] text-sm" data-testid="club-popup-error">{error}</p>}
      <p className="mt-3 text-white/50 text-sm">{t.note}</p>
    </div>
  );
}
